
import React, { useState, useEffect } from 'react';
import { Cookie, X, Shield } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from './Button';
import { Link } from 'react-router-dom';
import { ROUTES } from '../../constants';

export const CookieConsent: React.FC = () => {
    const { t } = useLanguage();
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const consent = localStorage.getItem('ctc-cookie-consent');
        if (!consent) {
            // Show banner after 1 second delay
            setTimeout(() => setIsVisible(true), 1000);
        }
    }, []);

    const handleAccept = () => {
        const consent = {
            necessary: true,
            timestamp: new Date().toISOString()
        };
        localStorage.setItem('ctc-cookie-consent', JSON.stringify(consent));
        setIsVisible(false);
    };

    if (!isVisible) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-end justify-center p-4 pointer-events-none">
            {/* Backdrop */}
            <div
                className={`absolute inset-0 bg-black/60 backdrop-blur-sm pointer-events-auto transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
            />

            {/* Cookie Banner */}
            <div className={`relative w-full max-w-3xl bg-gradient-to-br from-[#0f1623] to-[#1a2332] border border-blue-500/30 rounded-2xl shadow-2xl overflow-hidden pointer-events-auto transform transition-all duration-500 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`}>

                {/* Animated Background Pattern */}
                <div className="absolute inset-0 opacity-5">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500 rounded-full blur-3xl animate-pulse" />
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-green-500 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
                </div>

                {/* Content */}
                <div className="relative p-6 md:p-8">

                    {/* Header */}
                    <div className="flex items-start justify-between mb-6">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-blue-600/20 rounded-xl flex items-center justify-center">
                                <Cookie className="text-blue-400" size={24} />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                    {t('cookies.title')}
                                    <Shield className="text-green-400" size={18} />
                                </h3>
                                <p className="text-xs text-green-400 mt-1 flex items-center gap-1">
                                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                                    Privacy-First Approach
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => setIsVisible(false)}
                            className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-white/5 rounded-lg"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {/* Privacy Message */}
                    <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 mb-6">
                        <p className="text-sm text-gray-300">
                            <strong className="text-green-400">Rispettiamo la tua Privacy.</strong> Utilizziamo esclusivamente <strong>cookie tecnici necessari</strong> per il funzionamento del sito.
                            <span className="text-green-400"> Non tracciamo la tua navigazione</span>, non usiamo Google Analytics né cookie di marketing.
                        </p>
                    </div>

                    {/* Simple Description */}
                    <p className="text-gray-400 text-sm mb-6">
                        Cliccando "Ho capito", accetti l'utilizzo dei cookie tecnici strettamente necessari. Per maggiori informazioni, consulta la nostra{' '}
                        <Link to={ROUTES.COOKIE} className="text-blue-400 hover:underline">Cookie Policy</Link> e{' '}
                        <Link to={ROUTES.PRIVACY} className="text-blue-400 hover:underline">Privacy Policy</Link>.
                    </p>

                    {/* Single Accept Button */}
                    <div className="flex flex-col sm:flex-row gap-3">
                        <Button
                            onClick={handleAccept}
                            className="flex-1 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-bold py-3 px-6 rounded-xl shadow-lg shadow-green-500/20"
                        >
                            <Shield size={18} className="mr-2" />
                            Ho Capito
                        </Button>
                    </div>

                    {/* Footer Note */}
                    <div className="mt-4 pt-4 border-t border-gray-800">
                        <p className="text-xs text-gray-500 text-center">
                            ChartTower Corp. OÜ • Ruunaoja 3, 11415 Tallinn, Estonia • GDPR Compliant
                        </p>
                    </div>

                </div>
            </div>
        </div>
    );
};
